import api, { getErrorMessage } from './api';

const integrationsService = {
  /**
   * Get rewards platform integration status (admin only)
   * @returns {Promise<object>} - Platform status
   */
  getRewardsStatus: async () => {
    try {
      const response = await api.get('/api/v1/rewards/status');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get Workvivo integration status for organizations (admin only)
   * @returns {Promise<Array>} - Organizations with Workvivo status
   */
  getWorkvivoStatus: async () => {
    try {
      const response = await api.get('/api/v1/admin/organizations/');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Sync Workvivo users for a specific organization (admin only)
   * @param {string} orgId - Organization ID
   * @returns {Promise<object>} - Sync result with users_synced count
   */
  syncWorkvivoUsers: async (orgId) => {
    try {
      // Use the Workvivo setup complete endpoint to re-sync users
      const response = await api.post('/api/v1/workvivo/complete-setup', {
        organization_id: orgId,
        import_users: true,
        import_teams: true,
      });
      const data = response.data?.data || response.data;
      return {
        users_synced: data?.users_imported || 0,
        teams_synced: data?.teams_imported || 0,
      };
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Sync Workvivo team memberships (admin only)
   * Fetches team memberships for all users from Workvivo
   * @returns {Promise<object>} - Sync result with memberships_created count
   */
  syncWorkvivoTeamMemberships: async () => {
    try {
      const response = await api.post('/api/v1/workvivo/sync-team-memberships');
      return response.data?.data || response.data || { memberships_created: 0, memberships_updated: 0 };
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Sync all Workvivo organizations (admin only)
   * @returns {Promise<object>} - Sync result with organizations_synced count
   */
  syncAllWorkvivoOrganizations: async () => {
    try {
      // Get all organizations first
      const orgsResponse = await api.get('/api/v1/admin/organizations/');
      const orgs = orgsResponse.data || [];

      // Filter to only Workvivo-connected orgs
      const workvivoOrgs = orgs.filter(org =>
        org.workvivo_tenant_url && org.workvivo_scim_token
      );

      let totalUsersSynced = 0;
      let organizationsSynced = 0;

      // Sync each org (in sequence to avoid overwhelming the API)
      for (const org of workvivoOrgs) {
        try {
          const result = await integrationsService.syncWorkvivoUsers(org.id);
          totalUsersSynced += result.users_synced || 0;
          organizationsSynced++;
        } catch (err) {
          console.error(`Failed to sync org ${org.id}:`, err);
        }
      }

      return {
        organizations_synced: organizationsSynced,
        total_users_synced: totalUsersSynced,
      };
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },
};

export default integrationsService;
